import { useState, useEffect } from 'react';
import * as mm from '@magenta/music/esm/music_rnn';
import type { 
  DrumInstrument, 
  DrumGridData, 
  TimeSignature, 
  NoteDivision 
} from '../types';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import DrumRNNSeedConverter from './DrumRNNSeedConverter';
import { devLog } from '../utils/devLog';

// Time signatures configuration
const TIME_SIGNATURES: Record<TimeSignature, { beats: number; base: number }> = {
  '3/4': { beats: 3, base: 4 },
  '4/4': { beats: 4, base: 4 },
  '6/8': { beats: 6, base: 8 }
};

// Calculate steps per bar based on time signature and note division
const calcStepsPerBar = (timeSig: TimeSignature, noteDiv: NoteDivision): number => {
  const timeSignature = TIME_SIGNATURES[timeSig];
  if (!timeSignature) {
    console.warn(`Unsupported time signature: ${timeSig}, defaulting to 4/4`);
    return 16; // default to 16 steps for 4/4 with 16n
  }
  const { beats, base } = timeSignature;
  const noteLengthMap: Record<NoteDivision, number> = { '4n': 4, '8n': 8, '16n': 16 };
  const noteValue = noteLengthMap[noteDiv] || 16;
  return beats * (noteValue / base);
};

const MODEL_URL = process.env.PUBLIC_URL + '/models/drum_kit_rnn';

interface DrumRNNButtonProps {
  instruments: DrumInstrument[];
  grid?: DrumGridData; // 當前網格數據
  setGrid: (grid: DrumGridData) => void;
  setStepsPerBar: (steps: number) => void;
  timeSignature?: TimeSignature;
  noteDivision?: NoteDivision;
  stepsPerBar?: number;
  temperature?: number;
  presetPattern?: string | null; // preset pattern key
  useSeedFromPattern?: boolean; // 是否使用pattern作為seed
  seedOptions?: { // seed選項
    velocity?: number;
    repeatCount?: number;
    targetSteps?: number;
    addVariation?: boolean;
  };
}

interface QuantizationInfo {
  stepsPerQuarter: number;
  quantizeNoteValueLength: number;
}

interface Note {
  pitch: number;
  velocity?: number;
  quantizedStartStep: number;
  quantizedEndStep?: number;
  startTime?: number;
  endTime?: number;
}

interface MusicSequence {
  notes: Note[];
  totalTime: number;
  quantizationInfo?: QuantizationInfo;
}

const DrumRNNButton: React.FC<DrumRNNButtonProps> = ({ 
  instruments, 
  grid, // 新增：當前網格數據
  setGrid, 
  setStepsPerBar,
  timeSignature = '4/4',
  noteDivision = '16n',
  stepsPerBar = 16,
  temperature = 2.0,
  presetPattern = null, // 新增：preset pattern key
  useSeedFromPattern = false, // 新增：是否使用pattern作為seed
  seedOptions = {} // 新增：seed選項
}) => {
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<mm.MusicRNN | null>(null);
  const [multiplier, setMultiplier] = useState(1); // 1x, 2x, 4x, 8x
  const [baseStepsPerBar, setBaseStepsPerBar] = useState(16); // 記錄基礎步數
  const { t } = useLanguage();
  const { getClass } = useThemeContext();

  // 同步基礎步數：使用統一的calcStepsPerBar函數
  useEffect(() => {
    const newBaseSteps = calcStepsPerBar(timeSignature, noteDivision);
    setBaseStepsPerBar(newBaseSteps);
  }, [timeSignature, noteDivision]);

  // 當回到 preset pattern 或 demo 時重置倍數
  useEffect(() => {
    // 如果當前步數等於基礎步數，說明沒有倍數，重置為 1x
    if (stepsPerBar === baseStepsPerBar) {
      setMultiplier(1);
    }
  }, [stepsPerBar, baseStepsPerBar]);

  // 主題類別獲取函數
  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        buttonDisabled: 'bg-gray-400 text-gray-200 cursor-not-allowed',
        border: 'border-gray-300',
      };
      return fallback[type] || '';
    }
  };

  // MIDI音符到樂器ID的映射
  const mapMidiToInstrumentId = (pitch: number): string | null => {
    const midiMap: Record<number, string> = {
      // 基本鼓組映射
      36: 'BD', // Bass Drum (Kick)
      38: 'SD', // Snare Drum
      42: 'CH', // Closed Hi-Hat
      46: 'OH', // Open Hi-Hat
      49: 'CR', // Crash Cymbal
      51: 'RD', // Ride Cymbal
      45: 'T1', // Low Tom
      47: 'T2', // Mid Tom
      48: 'T3', // High Tom
      50: 'T2', // High Tom alternative
      
      // 擴展映射
      37: 'SD', // Cross Stick (mapped to Snare)
      39: 'CP', // Hand Clap
      56: 'CB', // Cowbell
      54: 'SH', // Tambourine/Shaker
      53: 'RD', // Ride Bell
      57: 'CR', // Crash 2
      59: 'RD', // Ride 2
      
      // Percussion映射
      75: 'CP', // Claves
      76: 'CB', // Wood Block High
      77: 'CB', // Wood Block Low
      85: 'SH', // Castanets
    };
    
    const id = midiMap[pitch];
    return instruments.some(inst => inst.id === id) ? id : null;
  };

  // 根據時間簽名和音符類型調整量化參數
  const getQuantizationInfo = (): QuantizationInfo => {
    const { beats, base } = TIME_SIGNATURES[timeSignature];
    const noteLengthMap: Record<NoteDivision, number> = {
      '4n': 4,
      '8n': 8,
      '16n': 16
    };
    
    const noteValue = noteLengthMap[noteDivision] || 16;
    const stepsPerQuarter = noteValue / 4; // 16n -> 4, 8n -> 2, 4n -> 1
    
    return {
      stepsPerQuarter: Math.max(1, stepsPerQuarter),
      quantizeNoteValueLength: noteValue
    };
  };

  // 從序列生成網格
  const generateGridFromSequence = (sequence: MusicSequence, steps: number): DrumGridData => {
    const grid: DrumGridData = {};
    
    // 初始化網格
    instruments.forEach(({ id }) => {
      grid[id] = new Array(steps).fill(null);
    });

    // 處理音符序列
    for (const note of sequence.notes) {
      // 移除模運算，直接使用quantizedStartStep
      const step = Math.floor(note.quantizedStartStep);
      const drumId = mapMidiToInstrumentId(note.pitch);
      
      // 只處理在有效範圍內的步數
      if (drumId && grid[drumId] && step >= 0 && step < steps) {
        // 根據velocity決定力度標記
        const velocity = note.velocity || 80;
        let hitType = 'x';
        
        if (velocity >= 100) {
          hitType = 'X'; // 重音
        } else if (velocity >= 80) {
          hitType = 'x'; // 正常
        } else if (velocity >= 60) {
          hitType = 'g'; // 鬼音符 (ghost note)
        } else {
          hitType = 'f'; // 輕音
        }
        
        grid[drumId][step] = hitType;
      }
    }
    
    return grid;
  };

  // 生成種子序列 (可選：使用preset pattern或當前網格作為種子)
  const generateSeedSequence = (): MusicSequence => {
    const quantizationInfo = getQuantizationInfo();
    const totalSteps = baseStepsPerBar * multiplier; // 使用基礎步數計算
    
    // 如果指定使用preset pattern作為seed
    if (useSeedFromPattern && presetPattern) {
      devLog(`🎲 DrumRNN: Using preset pattern "${presetPattern}" as seed`);
      
      const seedFromPattern = DrumRNNSeedConverter.convertPatternToSeed(
        presetPattern,
        {
          velocity: seedOptions.velocity || 80,
          repeatCount: seedOptions.repeatCount || 1,
          targetSteps: totalSteps, // 傳遞總步數，確保在倍數模式下重複pattern
          addVariation: seedOptions.addVariation !== false,
          ...seedOptions
        }
      );
      
      // 調整seed的時間和量化信息以匹配當前設置
      return {
        ...seedFromPattern,
        quantizationInfo: quantizationInfo
      };
    }
    
    // 如果指定使用當前網格作為seed
    if (useSeedFromPattern && !presetPattern) {
      devLog('🎲 DrumRNN: Using current grid as seed');
      
      const seedFromGrid = DrumRNNSeedConverter.convertGridToSeed(
        grid || {}, 
        instruments, 
        timeSignature, 
        noteDivision
      );
      
      return {
        ...seedFromGrid,
        quantizationInfo: quantizationInfo
      };
    }
    
    // 默認：空種子以獲得更多變化
    const emptySeed: MusicSequence = {
      notes: [],
      totalTime: totalSteps / 4, // 轉換為quarter notes
      quantizationInfo: quantizationInfo
    };
    
    devLog('🎲 DrumRNN: Using empty seed for random generation');
    return emptySeed;
  };

  // 主要生成函數
  const handleGenerate = async (): Promise<void> => {
    if (loading) return;
    setLoading(true);

    try {
      if (!model) {
        const rnn = new mm.MusicRNN(MODEL_URL);
        await rnn.initialize();
        setModel(rnn);
      }
      
      const rnn = model || new mm.MusicRNN(MODEL_URL);
      if (!model) {
        await rnn.initialize();
        setModel(rnn);
      }

      const seed = generateSeedSequence();
      const totalSteps = baseStepsPerBar * multiplier;
      const adjustedSteps = totalSteps;
      const adjustedTemperature = Math.max(temperature, 1.0); // 確保溫度至少為1.0

      devLog(`🎲 DrumRNN: Generating ${adjustedSteps} steps (${multiplier}x multiplier, base: ${baseStepsPerBar})`);

      // 嘗試使用不同的API參數來確保正確的序列長度
      devLog(`🔧 Calling continueSequence with seed.totalTime=${seed.totalTime}, steps=${adjustedSteps}, temperature=${adjustedTemperature}`);
      
      let result: MusicSequence;
      try {
        devLog('🧪 Trying multiple generation approaches...');
        
        // 方法1: 標準continueSequence
        devLog('🔄 Method 1: Standard continueSequence');
        result = await rnn.continueSequence(
          seed,
          adjustedSteps,
          adjustedTemperature
        ) as MusicSequence;
        
        devLog(`📊 Method 1 result: ${result.notes.length} notes`);
        
        // 如果結果不理想，嘗試其他方法
        if (result.notes.length < adjustedSteps * 0.1) {
          devLog('🔄 Method 2: Trying different totalTime...');
          
          const modifiedSeed: MusicSequence = {
            ...seed,
            totalTime: Math.max(seed.totalTime, adjustedSteps / 4) // 確保totalTime足夠
          };
          
          const result2 = await rnn.continueSequence(
            modifiedSeed,
            adjustedSteps,
            adjustedTemperature
          ) as MusicSequence;
          
          devLog(`📊 Method 2 result: ${result2.notes.length} notes`);
          
          if (result2.notes.length > result.notes.length) {
            devLog('✅ Method 2 worked better!');
            result = result2;
          }
        }
        
        // 方法3: 嘗試更短的seed
        if (result.notes.length < adjustedSteps * 0.15) {
          devLog('🔄 Method 3: Trying generation with shorter seed...');
          
          const shorterSeed: MusicSequence = {
            notes: seed.notes.slice(0, Math.max(1, Math.floor(seed.notes.length / 2))),
            totalTime: Math.max(0.25, seed.totalTime / 2),
            quantizationInfo: seed.quantizationInfo
          };
          
          const result3 = await rnn.continueSequence(
            shorterSeed,
            adjustedSteps,
            adjustedTemperature
          ) as MusicSequence;
          
          devLog(`📊 Method 3 result: ${result3.notes.length} notes`);
          
          if (result3.notes.length > result.notes.length) {
            devLog('✅ Method 3 worked better!');
            result = result3;
          }
        }
        
        // 方法4: 純隨機生成作為對比
        if (result.notes.length < 3) {
          devLog('🔄 Method 4: Pure random generation for comparison...');
          
          const randomSeed: MusicSequence = {
            notes: [],
            totalTime: 0.25,
            quantizationInfo: seed.quantizationInfo
          };
          
          const result4 = await rnn.continueSequence(
            randomSeed,
            adjustedSteps,
            adjustedTemperature
          ) as MusicSequence;
          
          devLog(`📊 Method 4 (random) result: ${result4.notes.length} notes`);
          
          if (result4.notes.length >= 3) {
            result = result4;
          }
        }
        
        // 方法5: 嘗試極高溫度
        if (result.notes.length < 3) {
          devLog('🔄 Method 5: Testing with extreme high temperature...');
          
          const highTempResult = await rnn.continueSequence(
            seed,
            adjustedSteps,
            2.0
          ) as MusicSequence;
          
          devLog(`📊 Method 5 (extreme temp) result: ${highTempResult.notes.length} notes`);
          
          if (highTempResult.notes.length > result.notes.length) {
            result = highTempResult;
          }
        }
        
      } catch (error) {
        console.error('All generation methods failed:', error);
        throw error;
      }

      // 修復totalTime問題
      if (result.totalTime === 0 && result.notes.length > 0) {
        // 手動計算正確的totalTime
        const maxEndTime = Math.max(...result.notes.map(note => note.endTime || note.startTime || 0 + 0.25));
        const calculatedTotalTime = Math.max(maxEndTime, adjustedSteps / 4);
        const originalTotalTime = result.totalTime;
        
        result.totalTime = calculatedTotalTime;
        
        // 確保所有notes都有正確的startTime和endTime
        result.notes.forEach(note => {
          if (!note.startTime && note.quantizedStartStep !== undefined) {
            note.startTime = note.quantizedStartStep / 4;
          }
          if (!note.endTime && note.startTime !== undefined) {
            note.endTime = note.startTime + 0.25; // 默認四分之一拍長度
          }
        });
        
        devLog(`🔧 Fixing totalTime: ${originalTotalTime} → ${calculatedTotalTime} (requested ${adjustedSteps} steps = ${adjustedSteps/4} quarters)`);
      }

      // 調試信息輸出
      devLog('🧠 RNN Generation Debug Info:');
      devLog(`📥 Input Seed - Notes: ${seed.notes.length}, Steps: ${seed.totalTime * (seed.quantizationInfo?.stepsPerQuarter || 4)}`);
      devLog(`⚙️ Generation Parameters - Temperature: ${adjustedTemperature}, Requested Steps: ${adjustedSteps}`);
      devLog(`📤 RNN Output - Notes: ${result.notes.length}, Total Time: ${result.totalTime}`);
      
      // 深入分析seed
      devLog('🔍 Detailed Seed Analysis:');
      devLog('Input Seed Object:', seed);
      devLog('Quantization Info:', seed.quantizationInfo);
      if (seed.notes.length > 0) {
        devLog('First few seed notes:', seed.notes.slice(0, 5));
        
        // 檢查seed的完整性
        const seedTimeRange = { min: Infinity, max: -Infinity };
        const invalidSeedNotes: Array<{ index: number; issue: string; note: Note }> = [];
        const seedNotesByPitch: Record<number, number> = {};
        
        seed.notes.forEach((note, index) => {
          // 檢查每個seed note的合法性
          if (!note.pitch || note.pitch < 0 || note.pitch > 127) {
            invalidSeedNotes.push({ index, issue: 'invalid pitch', note });
          }
          if ((!note.quantizedStartStep && note.quantizedStartStep !== 0) || note.quantizedStartStep < 0) {
            invalidSeedNotes.push({ index, issue: 'invalid quantizedStartStep', note });
          }
          
          // 統計樂器分布
          if (!seedNotesByPitch[note.pitch]) seedNotesByPitch[note.pitch] = 0;
          seedNotesByPitch[note.pitch]++;
          
          // 計算時間範圍
          const startTime = note.startTime || (note.quantizedStartStep / 4);
          const endTime = note.endTime || startTime + 0.25;
          seedTimeRange.min = Math.min(seedTimeRange.min, startTime);
          seedTimeRange.max = Math.max(seedTimeRange.max, endTime);
        });
        
        const instrumentNames: Record<number, string> = {36: 'BD', 38: 'SD', 42: 'CH', 46: 'OH', 39: 'CP', 49: 'CR'};
        devLog('🌱 Seed Notes Analysis:');
        Object.entries(seedNotesByPitch).forEach(([pitch, count]) => {
          devLog(`  ${instrumentNames[parseInt(pitch)] || `MIDI${pitch}`}: ${count} notes`);
        });
        
        devLog(`📐 Seed Time Range: ${seedTimeRange.min.toFixed(2)} - ${seedTimeRange.max.toFixed(2)} (duration: ${(seedTimeRange.max - seedTimeRange.min).toFixed(2)})`);
        devLog(`⏱️ Seed Total Time vs Calculated: ${seed.totalTime} vs ${seedTimeRange.max.toFixed(2)}`);
        
        // 檢查步數分布
        const expectedSteps = seed.totalTime * (seed.quantizationInfo?.stepsPerQuarter || 4);
        const seedStepsWithNotes = new Set(seed.notes.map(n => n.quantizedStartStep)).size;
        devLog(`📊 Seed Steps: ${seedStepsWithNotes}/${expectedSteps} steps have notes (${(seedStepsWithNotes/expectedSteps*100).toFixed(1)}%)`);
        
        if (invalidSeedNotes.length > 0) {
          console.error('❌ Invalid notes in seed:', invalidSeedNotes);
        } else {
          devLog('✅ All seed notes appear valid');
        }
        
        // 量化兼容性檢查
        devLog('🔧 Quantization Compatibility Check:');
        devLog(`  Steps per quarter: ${seed.quantizationInfo?.stepsPerQuarter}`);
        devLog(`  Note value length: ${seed.quantizationInfo?.quantizeNoteValueLength}`);
        devLog(`  Expected total steps: ${expectedSteps}`);
        devLog(`  Actual max step in seed: ${Math.max(...seed.notes.map(n => n.quantizedEndStep || 0))}`);
      } else {
        devLog('⚠️ Empty seed - using random generation');
      }
      
      // 分析輸出結果
      if (result.notes && result.notes.length > 0) {
        // 統計輸出notes
        const outputNotesByPitch: Record<number, number> = {};
        const outputNotesByStep: Record<number, number> = {};
        result.notes.forEach(note => {
          // 樂器統計
          if (!outputNotesByPitch[note.pitch]) outputNotesByPitch[note.pitch] = 0;
          outputNotesByPitch[note.pitch]++;
          // 步數統計
          const step = note.quantizedStartStep;
          if (!outputNotesByStep[step]) outputNotesByStep[step] = 0;
          outputNotesByStep[step]++;
        });
        
        const instrumentNames: Record<number, string> = {36: 'BD', 38: 'SD', 42: 'CH', 46: 'OH', 39: 'CP', 49: 'CR'};
        devLog('🎵 Output Notes by Instrument:');
        Object.entries(outputNotesByPitch).forEach(([pitch, count]) => {
          devLog(`  ${instrumentNames[parseInt(pitch)] || `MIDI${pitch}`}: ${count} notes`);
        });
        
        const nonEmptySteps = Object.keys(outputNotesByStep).length;
        devLog(`📊 Pattern Density: ${nonEmptySteps}/${adjustedSteps} steps have notes (${(nonEmptySteps/adjustedSteps*100).toFixed(1)}%)`);
        
        if (result.notes.length === 0) {
          console.error('❌ No notes generated! This indicates:');
          console.error('   - Temperature was too low (was adjusted to ' + adjustedTemperature + ')');
          console.error('   - Model may not be compatible with current environment');
          console.error('   - Possible quantization format mismatch');
          console.error('   - Try reloading the page to reinitialize the model');
        } else if (result.notes.length < 5) {
          console.warn('⚠️ Very few notes generated! This might indicate:');
          console.warn('   - Temperature still too low (try 1.0-1.5)');
          console.warn('   - Seed pattern not compatible with model training');
          console.warn('   - Model expecting different quantization');
          console.warn('   - Try random generation (no seed) to test model');
        }
        
        // 深入檢查RNN結果
        devLog('🔍 RNN Result Deep Analysis:');
        devLog('Complete result object:', result);
        if (result.notes && result.notes.length > 0) {
          const resultTimeRange = { min: Infinity, max: -Infinity };
          const invalidResultNotes: Array<{ index: number; issue: string; note: Note }> = [];
          
          result.notes.forEach((note, index) => {
            // 檢查每個輸出note的合法性
            if (!note.pitch || note.pitch < 0 || note.pitch > 127) {
              invalidResultNotes.push({ index, issue: 'invalid pitch', note });
            }
            if ((!note.quantizedStartStep && note.quantizedStartStep !== 0) || note.quantizedStartStep < 0) {
              invalidResultNotes.push({ index, issue: 'invalid quantizedStartStep', note });
            }
            
            // 計算時間範圍
            const startTime = note.startTime || 0;
            const endTime = note.endTime || startTime + 0.25; // 設定默認長度
            resultTimeRange.min = Math.min(resultTimeRange.min, startTime);
            resultTimeRange.max = Math.max(resultTimeRange.max, endTime);
          });
          
          devLog(`📐 Result Time Range: ${resultTimeRange.min.toFixed(2)} - ${resultTimeRange.max.toFixed(2)}`);
          devLog(`⏱️ Result Total Time: ${result.totalTime} (should be: ${resultTimeRange.max.toFixed(2)})`);
          devLog(`🎯 Expected minimum time: ${adjustedSteps / 4} quarters (${adjustedSteps} steps)`);
          
          // 檢查每個note的時間信息
          if (result.notes.length <= 5) { // 只顯示少數notes的詳細信息
            devLog('📝 Individual notes timing:');
            result.notes.forEach((note, i) => {
              devLog(`  Note ${i}: start=${note.startTime}, end=${note.endTime}, pitch=${note.pitch}, step=${note.quantizedStartStep}`);
            });
          }
          
          if (invalidResultNotes.length > 0) {
            console.error('❌ Invalid notes in RNN result:', invalidResultNotes);
          }
        }
        
        if (result.totalTime === 0) {
          console.error('❌ Critical: RNN output totalTime is 0! This indicates:');
          console.error('   - Model failed to process the input properly');
          console.error('   - Quantization mismatch between seed and model');
          console.error('   - Possible model loading issue');
          console.error('🔧 Debugging suggestions:');
          console.error('   1. Try higher temperature (0.8-1.2)');
          console.error('   2. Try random generation (no seed)');
          console.error('   3. Check if model loaded correctly');
          console.error('   4. Verify seed format matches model expectations');
          
          // 檢查模型狀態
          if (rnn && rnn.isInitialized) {
            devLog('✅ Model appears initialized');
            devLog('Model details:', {
              initialized: rnn.isInitialized()
            });
          } else {
            console.error('❌ Model may not be properly initialized');
          }
          
          // 建議測試純隨機生成
          console.error('🎯 Suggested next test: Try pure random generation (no seed) to isolate the issue');
        } else {
          devLog('✅ RNN generation successful with proper totalTime');
        }
        
        const newGrid = generateGridFromSequence(result, totalSteps);
        
        // 更新：設定更新grid 和stepsPerBar
        setGrid(newGrid);
        if (setStepsPerBar && totalSteps !== stepsPerBar) {
          setStepsPerBar(totalSteps);
          devLog(`🎲 DrumRNN: Updated stepsPerBar from ${stepsPerBar} to ${totalSteps}`);
        }
      }
        
    } catch (error) {
      console.error('DrumRNN generation failed:', error);
      // 可以在這裡添加錯誤處理和用戶提示
    } finally {
      setLoading(false);
    }
  };

  // 循環切換倍數：1x -> 2x -> 4x -> 8x -> 1x
  const toggleMultiplier = (): void => {
    setMultiplier(prev => {
      switch(prev) {
        case 1: return 2;
        case 2: return 4;
        case 4: return 8;
        case 8: return 1;
        default: return 1;
      }
    });
  };

  // 樣式
  const buttonClass = `
    flex items-center justify-center rounded text-sm transition-all duration-200 
    hover:scale-105 active:scale-95 transform-gpu px-2 py-1 md:px-3 md:py-1.5 md:space-x-1
    ${loading 
      ? getThemeClass('buttonDisabled') 
      : getThemeClass('button')
    } 
    ${loading ? 'cursor-not-allowed' : 'cursor-pointer'}
  `.trim();

  const multiplierButtonClass = `
    ml-1 px-1 py-0.5 text-xs rounded border border-current opacity-70 hover:opacity-100
    transition-opacity duration-200
  `.trim();

  const currentSteps = baseStepsPerBar * multiplier;
  
  // 決定種子圖標
  const getSeedIndicator = (): string => {
    if (useSeedFromPattern && presetPattern) return '🌱'; // pattern seed
    if (useSeedFromPattern && !presetPattern) return '🔄'; // grid seed
    return '🎲'; // random
  };
  
  const buttonText = loading 
    ? t('drumRNN.generating') 
    : `${t('drumRNN.generate')} ${multiplier > 1 ? `(${multiplier}x)` : ''}`;
  
  const mobileText = loading 
    ? t('generating') 
    : `RNN${multiplier > 1 ? ` ${multiplier}x` : ''}`;

  return (
    <div className="flex items-center space-x-1">
      <button
        className={buttonClass}
        onClick={handleGenerate}
        disabled={loading}
        title={`${loading ? t('drumRNN.generating') : t('drumRNN.tooltip')} (${currentSteps} steps)`}
      >
        <span className="text-base">{getSeedIndicator()}</span>
        <span className="hidden md:inline">
          {buttonText}
        </span>
        <span className="md:hidden">
          {mobileText}
        </span>
      </button>
      
      {/* 倍數切換按鈕 */}
      <button
        className={multiplierButtonClass}
        onClick={toggleMultiplier}
        disabled={loading}
        title={`切換生成長度: ${multiplier}x (當前: ${currentSteps} steps)`}
      >
        {multiplier}x
      </button>
    </div>
  );
};

export default DrumRNNButton; 